import { apiService } from '../apiService';
import type { ApiResponse, ApiError } from '../../../../types';

export type { ApiError, ApiResponse } from '../../../../types';

/**
 * BaseApiService (Standard Astrolabe)
 * Fournit des méthodes HTTP protégées pour les services spécialisés.
 * Les noms sont préfixés par 'request' pour éviter les conflits avec les méthodes métier.
 */
export class BaseApiService {
  protected basePath: string;

  constructor(basePath: string) {
    this.basePath = basePath;
  }

  /**
   * Construire une query string à partir d'un objet
   */
  protected buildQueryString(params: Record<string, any>): string {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
  }

  /**
   * GET request générique
   */
  protected async requestGet<T>(endpoint: string, includeAuth: boolean = true): Promise<ApiResponse<T>> {
    try {
      const url = `${this.basePath}${endpoint}`;
      const response = await apiService.get<T>(url, includeAuth);
      return response;
    } catch (error: any) {
      return {
        success: false,
        data: null as any,
        error: error.message || 'Erreur lors de la requête GET'
      };
    }
  }

  /**
   * POST request générique
   */
  protected async requestPost<T>(endpoint: string, data?: any, includeAuth: boolean = true): Promise<ApiResponse<T>> {
    try {
      const url = `${this.basePath}${endpoint}`;
      const response = await apiService.post<T>(url, data, includeAuth);
      return response;
    } catch (error: any) {
      const apiError: ApiError = {
        code: error.code || 'UNKNOWN_ERROR',
        message: error.message || 'Erreur lors de la requête POST',
        details: JSON.stringify({
          endpoint: `${this.basePath}${endpoint}`,
          method: 'POST',
          timestamp: new Date().toISOString()
        }),
        timestamp: new Date().toISOString()
      };

      return {
        success: false,
        data: null as any,
        error: apiError.message
      };
    }
  }

  /**
   * PUT request générique
   */
  protected async requestPut<T>(endpoint: string, data: any, includeAuth: boolean = true): Promise<ApiResponse<T>> {
    try {
      const url = `${this.basePath}${endpoint}`;
      const response = await apiService.put<T>(url, data, includeAuth);
      return response;
    } catch (error: any) {
      return {
        success: false,
        data: null as any,
        error: error.message || 'Erreur lors de la requête PUT'
      };
    }
  }

  /**
   * PATCH request générique
   */
  protected async requestPatch<T>(endpoint: string, data?: any, includeAuth: boolean = true): Promise<ApiResponse<T>> {
    try {
      const url = `${this.basePath}${endpoint}`;
      const response = await apiService.patch<T>(url, data, includeAuth);
      return response;
    } catch (error: any) {
      return {
        success: false,
        data: null as any,
        error: error.message || 'Erreur lors de la requête PATCH'
      };
    }
  }

  /**
   * DELETE request générique
   */
  protected async requestDelete<T = boolean>(endpoint: string, includeAuth: boolean = true): Promise<ApiResponse<T>> {
    try {
      const url = `${this.basePath}${endpoint}`;
      const result = await apiService.delete(url, includeAuth);

      return {
        success: result.success,
        data: (result.success as any) as T,
        message: result.message
      };
    } catch (error: any) {
      return {
        success: false,
        data: null as any,
        error: error.message || 'Erreur lors de la requête DELETE'
      };
    }
  }
}
